import type { DeforestationLayerStates } from "../types/deforestation";

export type ContextFillLayerId =
  | "context-capad-fill"
  | "context-ramsar-fill"
  | "context-world-heritage-fill"
  | "context-national-heritage-fill"
  | "context-commonwealth-heritage-fill"
  | "context-rfa-fill"
  | "context-regulated-veg-fill"
  | "context-bushfire-history-fill"
  | "context-koala-habitat-fill";

export interface ContextLayerMeta {
  // Maplibre fill-layer id, used for click queries and color sync.
  fillLayerId: ContextFillLayerId;
  // Display label shown in the popup chip + detail header.
  label: string;
  // DeforestationLayerStates key — used to read the user-chosen color.
  stateKey: keyof DeforestationLayerStates;
  // Human label for the feature title — picks the first non-empty prop.
  titleProps: string[];
  // Properties to render as `key: value` rows in the detail table (in order).
  detailProps: { key: string; label: string }[];
}

export const CONTEXT_LAYER_META: Record<ContextFillLayerId, ContextLayerMeta> = {
  "context-capad-fill": {
    fillLayerId: "context-capad-fill",
    label: "Protected Area",
    stateKey: "protectedMatters",
    titleProps: ["NAME", "name"],
    detailProps: [
      { key: "TYPE", label: "Type" },
      { key: "TYPE_ABBR", label: "Type (abbr)" },
      { key: "IUCN", label: "IUCN category" },
      { key: "STATE", label: "State" },
      { key: "AUTHORITY", label: "Authority" },
      { key: "GOVERNANCE", label: "Governance" },
      { key: "NRS_PA", label: "NRS protected area" },
      { key: "GAZ_DATE", label: "Gazetted" },
      { key: "LATEST_GAZ", label: "Latest gazettal" },
      { key: "GAZ_AREA", label: "Gazetted area (ha)" },
      { key: "GIS_AREA", label: "GIS area (ha)" },
      { key: "MGT_PLAN", label: "Mgmt plan" },
      { key: "EPBC", label: "EPBC" },
      { key: "PA_SYSTEM", label: "PA system" },
      { key: "PA_ID", label: "Protected area ID" },
    ],
  },
  "context-ramsar-fill": {
    fillLayerId: "context-ramsar-fill",
    label: "Ramsar Wetland",
    stateKey: "ramsarWetlands",
    titleProps: ["RAMSAR_NAM", "NAME", "name"],
    detailProps: [
      { key: "WETLAND_NA", label: "Wetland" },
      { key: "STATE", label: "State" },
      { key: "JURISDICTI", label: "Jurisdiction" },
      { key: "DESIGNATIO", label: "Designated" },
      { key: "GAZETTED_A", label: "Gazettal area (ha)" },
      { key: "AREA_HA", label: "Area (ha)" },
      { key: "TOTAL_SITE", label: "Total site (ha)" },
      { key: "REFCODE", label: "Reference code" },
      { key: "WETLANDSDB", label: "Wetlands DB" },
      { key: "METADATA_U", label: "Metadata URL" },
      { key: "SOURCE", label: "Source" },
    ],
  },
  "context-world-heritage-fill": {
    fillLayerId: "context-world-heritage-fill",
    label: "World Heritage",
    stateKey: "worldHeritage",
    titleProps: ["NAME", "name"],
    detailProps: [
      { key: "STATUS", label: "Status" },
      { key: "INSCRIBED", label: "Inscribed" },
      { key: "STATE", label: "State" },
      { key: "ADDRESS", label: "Address" },
      { key: "IUCN_MGT", label: "IUCN management" },
      { key: "BIOGEOPROV", label: "Biogeographic province" },
      { key: "NATURAL", label: "Natural criteria" },
      { key: "CULTURAL", label: "Cultural criteria" },
      { key: "HECTARES", label: "Area (ha)" },
      { key: "PLACE_ID", label: "Place ID" },
    ],
  },
  "context-national-heritage-fill": {
    fillLayerId: "context-national-heritage-fill",
    label: "National Heritage",
    stateKey: "nationalHeritage",
    titleProps: ["NAME", "name"],
    detailProps: [
      { key: "CLASS", label: "Class" },
      { key: "STATUS", label: "Status" },
      { key: "STATE", label: "State" },
      { key: "ADDRESS", label: "Address" },
      { key: "REGISTER_D", label: "Registered" },
      { key: "UPDATED", label: "Updated" },
      { key: "URL", label: "DCCEEW page" },
      { key: "AREA_HA", label: "Area (ha)" },
      { key: "PLACE_ID", label: "Place ID" },
    ],
  },
  "context-commonwealth-heritage-fill": {
    fillLayerId: "context-commonwealth-heritage-fill",
    label: "Commonwealth Heritage",
    stateKey: "commonwealthHeritage",
    titleProps: ["NAME", "name"],
    detailProps: [
      { key: "CLASS", label: "Class" },
      { key: "STATUS", label: "Status" },
      { key: "STATE", label: "State" },
      { key: "ADDRESS", label: "Address" },
      { key: "REGISTER", label: "Registered" },
      { key: "UPDATE_", label: "Updated" },
      { key: "AREA_HA", label: "Area (ha)" },
      { key: "PLACE_ID", label: "Place ID" },
    ],
  },
  "context-rfa-fill": {
    fillLayerId: "context-rfa-fill",
    label: "Regional Forest Agreement",
    stateKey: "regionalForestAgreements",
    titleProps: ["REG_NAM", "RFA_NAME", "NAME", "name"],
    detailProps: [
      { key: "REG_TYPE", label: "Boundary type" },
      { key: "STATE", label: "State" },
    ],
  },
  "context-regulated-veg-fill": {
    fillLayerId: "context-regulated-veg-fill",
    label: "Regulated Vegetation",
    stateKey: "regulatedVeg",
    titleProps: ["rvm_cat", "RVM_CAT"],
    detailProps: [
      { key: "rvm_cat", label: "Category" },
      { key: "version", label: "Version" },
    ],
  },
  "context-bushfire-history-fill": {
    fillLayerId: "context-bushfire-history-fill",
    label: "Bushfire",
    stateKey: "bushfireHistory",
    titleProps: ["fire_name", "Fire_Name", "FIRE_NAME"],
    detailProps: [
      { key: "ignition_date", label: "Ignition" },
      { key: "fire_type", label: "Type" },
      { key: "ignition_cause", label: "Cause" },
      { key: "area_ha", label: "Area (ha)" },
      { key: "state", label: "State" },
      { key: "agency", label: "Agency" },
    ],
  },
  "context-koala-habitat-fill": {
    fillLayerId: "context-koala-habitat-fill",
    label: "Koala Habitat",
    stateKey: "koalaHabitat",
    titleProps: ["GRIDCODE"],
    detailProps: [{ key: "GRIDCODE", label: "Grid code" }],
  },
};

export const CONTEXT_FILL_LAYER_IDS = Object.keys(
  CONTEXT_LAYER_META,
) as ContextFillLayerId[];

export function pickTitle(
  feature: GeoJSON.Feature,
  meta: ContextLayerMeta,
): string {
  const props = (feature.properties ?? {}) as Record<string, unknown>;
  for (const k of meta.titleProps) {
    const v = props[k];
    if (v !== undefined && v !== null && String(v).length > 0) {
      const s = String(v);
      // Bushfires often store ALL CAPS — title-case for display.
      return s.length <= 4
        ? s
        : s
            .toLowerCase()
            .replace(/\b\w/g, (c) => c.toUpperCase())
            .replace(/_/g, " ");
    }
  }
  return meta.label;
}

export function formatPropValue(v: unknown): string {
  if (v === null || v === undefined) return "—";
  const s = String(v);
  if (s.length === 0) return "—";
  // ISO date → YYYY-MM-DD
  if (/^\d{4}-\d{2}-\d{2}T/.test(s)) return s.slice(0, 10);
  // Numeric → 2 dp if it looks like a float, integer otherwise
  if (typeof v === "number") {
    return Number.isInteger(v) ? v.toString() : v.toFixed(1);
  }
  return s;
}
